import React, { useEffect, useMemo, useState } from 'react';
import HomeLayout from '../../asset/layout/HomeLayout';
import { getAllSupportQueries } from '../../actions/supportQueryAuth';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Stack,
  Grid,
  alpha,
  Tooltip,
} from '@mui/material';
import { motion } from 'framer-motion';
import dayjs from 'dayjs';
import CustomButton from '../../components/mui/CustomButton';
import SearchBar from '../../components/mui/SearchBar';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

const columns = [
  'Contact',
  'Email',
  'Phone',
  'Topic',
  'Message',
  'Submitted',
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.04, duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  }),
};

const PanelCard = ({ children, sx = {} }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: '24px',
      border: '1px solid',
      borderColor: alpha('#11409B', 0.08),
      bgcolor: '#fff',
      boxShadow: '0 4px 32px rgba(15, 23, 42, 0.06)',
      overflow: 'hidden',
      ...sx,
    }}
  >
    {children}
  </Card>
);

const StatCard = ({ icon: Icon, label, value, accent, gradient }) => (
  <PanelCard sx={{ height: '100%' }}>
    <Box sx={{ p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '14px',
            background: gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 8px 24px ${alpha(accent, 0.35)}`,
          }}
        >
          <Icon sx={{ color: '#fff', fontSize: 24 }} />
        </Box>
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: '#64748b',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontSize: '0.7rem',
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.75rem',
              color: '#0f172a',
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
            }}
          >
            {value ?? '—'}
          </Typography>
        </Box>
      </Stack>
    </Box>
  </PanelCard>
);

const display = (val) => (val != null && val !== '' ? val : '—');

const getInitials = (name) => {
  const src = name || '?';
  return src.charAt(0).toUpperCase();
};

const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  return dayjs(dateString).format('DD MMM YYYY, hh:mm A');
};

const SupportQuery = () => {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [countPerPage, setCountPerPage] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/');
    }
  }, [navigate]);

  const fetchQueries = () => {
    setLoading(true);
    dispatch(
      getAllSupportQueries(
        { page: currentPage - 1, size: countPerPage },
        (data) => {
          setQueries(data.content);
          setTotalElements(data.totalElements);
          setTotalPages(Math.max(data.totalPages, 1));
          setLoading(false);
        },
        () => setLoading(false)
      )
    );
  };

  useEffect(() => {
    fetchQueries();
  }, [dispatch, currentPage, countPerPage]);

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return queries;
    const q = searchTerm.toLowerCase();
    return queries.filter((item) =>
      [
        item?.fullName,
        item?.email,
        item?.phone,
        item?.topic,
        item?.message,
      ].some((field) => field?.toLowerCase?.().includes(q))
    );
  }, [queries, searchTerm]);

  const stats = useMemo(() => {
    const withEmail = queries.filter((row) => row?.email).length;
    const withPhone = queries.filter((row) => row?.phone).length;
    const thisMonth = queries.filter((row) =>
      row?.createdAt ? dayjs(row.createdAt).isSame(dayjs(), 'month') : false
    ).length;
    return {
      total: totalElements,
      onPage: queries.length,
      withEmail,
      withPhone,
      thisMonth,
    };
  }, [queries, totalElements]);

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleSearchClear = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const formatDateIndian = (dateString) => {
    const date = new Date(dateString);
    return format(date, 'dd/MM/yyyy', { timeZone: 'Asia/Kolkata' });
  };

  const exportToExcel = () => {
    const header = [
      'Full Name',
      'Email',
      'Phone',
      'Topic',
      'Message',
      'Created At',
      'Updated At',
    ];
    const rows = filteredData.map((row) => [
      row?.fullName,
      row?.email,
      row?.phone,
      row?.topic,
      row?.message,
      row?.createdAt ? formatDateIndian(row.createdAt) : '—',
      row?.updatedAt ? formatDateIndian(row.updatedAt) : '—',
    ]);
    rows.unshift(header);
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Support Queries');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([excelBuffer], { type: 'application/octet-stream' }),
      'Support_Queries.xlsx'
    );
  };

  const pageStart =
    totalElements === 0 ? 0 : (currentPage - 1) * countPerPage + 1;
  const pageEnd = Math.min(currentPage * countPerPage, totalElements);

  return (
    <HomeLayout>
      <Box
        sx={{
          minHeight: '100%',
          background:
            'linear-gradient(180deg, #f0f4fa 0%, #f5f7fa 35%, #eef2f7 100%)',
          mx: -3,
          mt: -3,
          px: 3,
          pt: 3,
          pb: 4,
        }}
      >
        <motion.div initial="hidden" animate="visible" variants={fadeUp}>
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              mb: 3,
              borderRadius: '24px',
              background:
                'linear-gradient(135deg, #0c3483 0%, #11409B 40%, #1e40af 70%, #2563eb 100%)',
              color: '#fff',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(17, 64, 155, 0.35)',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -50,
                right: -30,
                width: 200,
                height: 200,
                borderRadius: '50%',
                bgcolor: alpha('#fff', 0.06),
              }}
            />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={2}
              sx={{ position: 'relative', zIndex: 1 }}
            >
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <ContactSupportOutlinedIcon />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, letterSpacing: '0.1em', opacity: 0.85 }}
                  >
                    HELP & SUPPORT
                  </Typography>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}
                >
                  Support Query
                </Typography>
                <Typography sx={{ mt: 1, opacity: 0.82, maxWidth: 520 }}>
                  Review customer support submissions from the help center. Search
                  and export queries by contact, topic, or message.
                </Typography>
              </Box>
              <CustomButton
                variant="contained"
                onClick={exportToExcel}
                disabled={!filteredData.length}
                startIcon={<FileDownloadOutlinedIcon />}
                sx={{
                  height: '44px',
                  borderRadius: '12px',
                  fontWeight: 600,
                  textTransform: 'none',
                  bgcolor: alpha('#fff', 0.15),
                  border: `1px solid ${alpha('#fff', 0.25)}`,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: alpha('#fff', 0.22) },
                }}
              >
                Export queries
              </CustomButton>
            </Stack>
          </Box>
        </motion.div>

        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={0} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={QuestionAnswerOutlinedIcon}
                label="Total queries"
                value={stats.total}
                accent="#4f46e5"
                gradient="linear-gradient(135deg, #818cf8 0%, #4f46e5 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={1} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={EmailOutlinedIcon}
                label="With email (page)"
                value={stats.withEmail}
                accent="#2563eb"
                gradient="linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={2} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={PhoneOutlinedIcon}
                label="With phone (page)"
                value={stats.withPhone}
                accent="#059669"
                gradient="linear-gradient(135deg, #34d399 0%, #059669 100%)"
              />
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div custom={3} initial="hidden" animate="visible" variants={fadeUp}>
              <StatCard
                icon={ContactSupportOutlinedIcon}
                label="This month (page)"
                value={stats.thisMonth}
                accent="#7c3aed"
                gradient="linear-gradient(135deg, #a78bfa 0%, #7c3aed 100%)"
              />
            </motion.div>
          </Grid>
        </Grid>

        <motion.div custom={4} initial="hidden" animate="visible" variants={fadeUp}>
          <PanelCard sx={{ mb: 3, p: { xs: 2, md: 2.5 } }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <FilterListOutlinedIcon sx={{ color: '#11409B', fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>Filters</Typography>
            </Stack>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={10}>
                <SearchBar
                  onSearch={handleSearch}
                  searchTerm={searchTerm}
                  height="2.75rem"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <CustomButton
                  variant="outlined"
                  onClick={handleSearchClear}
                  fullWidth
                  sx={{
                    height: '2.75rem',
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: alpha('#11409B', 0.25),
                    color: '#11409B',
                    '&:hover': {
                      borderColor: '#11409B',
                      bgcolor: alpha('#11409B', 0.04),
                    },
                  }}
                >
                  Clear
                </CustomButton>
              </Grid>
            </Grid>
          </PanelCard>
        </motion.div>

        <motion.div custom={5} initial="hidden" animate="visible" variants={fadeUp}>
          {loading ? (
            <PanelCard>
              <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
                <CircularProgress sx={{ color: '#11409B' }} />
                <Typography sx={{ mt: 2, color: '#64748b' }}>
                  Loading support queries…
                </Typography>
              </Stack>
            </PanelCard>
          ) : filteredData.length > 0 ? (
            <TableContainer component={PanelCard} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 960 }}>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: alpha('#11409B', 0.04),
                      '& th': {
                        borderBottom: `1px solid ${alpha('#11409B', 0.08)}`,
                      },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column}
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          color: '#11409B',
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          py: 2,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {column}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.map((row) => (
                    <TableRow
                      key={row?.id}
                      sx={{
                        transition: 'background 0.2s',
                        '&:hover': { bgcolor: alpha('#11409B', 0.03) },
                        '& td': {
                          borderBottom: `1px solid ${alpha('#11409B', 0.06)}`,
                          py: 1.75,
                        },
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: alpha('#11409B', 0.12),
                              color: '#11409B',
                              fontWeight: 700,
                              fontSize: '0.875rem',
                            }}
                          >
                            {getInitials(row?.fullName)}
                          </Avatar>
                          <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
                            {display(row?.fullName)}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#475569' }}>
                          {display(row?.email)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#475569' }}>
                          {display(row?.phone)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={display(row?.topic)}
                          size="small"
                          sx={{
                            bgcolor: alpha('#11409B', 0.08),
                            color: '#11409B',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            maxWidth: 180,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Tooltip title={row?.message || ''} placement="top-start">
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#64748b',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {display(row?.message)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          {formatDateTime(row?.createdAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <PanelCard>
              <Stack alignItems="center" sx={{ py: 8, px: 3 }}>
                <ContactSupportOutlinedIcon
                  sx={{ fontSize: 56, color: '#cbd5e1', mb: 2 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  No support queries found
                </Typography>
                <Typography sx={{ color: '#64748b', mt: 1, textAlign: 'center' }}>
                  {searchTerm
                    ? 'Try adjusting your search on this page.'
                    : 'New help-center submissions will appear here.'}
                </Typography>
              </Stack>
            </PanelCard>
          )}
        </motion.div>

        {!loading && totalElements > 0 && (
          <motion.div custom={6} initial="hidden" animate="visible" variants={fadeUp}>
            <PanelCard sx={{ mt: 3, p: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Typography sx={{ color: '#64748b', fontWeight: 500 }}>
                  Showing {pageStart}–{pageEnd} of {totalElements}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Rows per page
                  </Typography>
                  <Box
                    component="select"
                    value={countPerPage}
                    onChange={(e) => {
                      setCountPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    sx={{
                      border: `1px solid ${alpha('#11409B', 0.2)}`,
                      borderRadius: '10px',
                      px: 1.5,
                      py: 0.75,
                      fontSize: '0.875rem',
                      bgcolor: '#fff',
                      outline: 'none',
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      sx={{
                        border: '1px solid',
                        borderColor: alpha('#11409B', 0.15),
                        borderRadius: '12px',
                        '&:hover': { bgcolor: alpha('#11409B', 0.06) },
                      }}
                    >
                      <KeyboardArrowLeftIcon />
                    </IconButton>
                    <Typography sx={{ fontWeight: 600, minWidth: 48, textAlign: 'center' }}>
                      {currentPage} / {totalPages}
                    </Typography>
                    <IconButton
                      onClick={() =>
                        setCurrentPage((p) => Math.min(p + 1, totalPages))
                      }
                      disabled={currentPage >= totalPages}
                      sx={{
                        border: '1px solid',
                        borderColor: alpha('#11409B', 0.15),
                        borderRadius: '12px',
                        '&:hover': { bgcolor: alpha('#11409B', 0.06) },
                      }}
                    >
                      <KeyboardArrowRightIcon />
                    </IconButton>
                  </Stack>
                </Stack>
              </Stack>
            </PanelCard>
          </motion.div>
        )}
      </Box>
    </HomeLayout>
  );
};

export default SupportQuery;
